import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { requestLogin, validateCode } from "../api/services";
import Logo from "../components/Logo";

interface LocationState {
  logoUrl?: string | null;
  companyName?: string | null;
  /** Set by ProtectedRoute — the page the visitor was trying to reach. */
  from?: { pathname?: string; search?: string; hash?: string };
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loginSent,
    pendingIdentifier,
    setLoginSent,
    setPendingIdentifier,
    setToken,
  } = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = location.state as LocationState;
  const passedLogo = state?.logoUrl ?? null;

  /**
   * Where to land after signing in.
   *
   * `from` is set by ProtectedRoute when it bounces an unauthenticated visit,
   * so an emailed deep link survives the login detour. Falls back to the
   * dashboard for a normal sign-in.
   *
   * Only same-origin paths are honoured: `from` reaches us through router
   * state, and treating an arbitrary value as a destination would let a
   * crafted link bounce someone off-site straight after authenticating.
   */
  const from = state?.from;
  const target =
    from && typeof from.pathname === "string"
      ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
      : null;
  const redirectTo =
    target && target.startsWith("/") && !target.startsWith("//")
      ? target
      : "/dashboard";

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await requestLogin(identifier);
      setPendingIdentifier(identifier);
      setLoginSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await validateCode(pendingIdentifier!, code);
      setToken(data.token);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setLoginSent(false);
    setPendingIdentifier(null);
    setError(null);
    setCode("");
  };

  return (
    <div className="mt-8 flex flex-col items-center px-3">
      <Logo src={passedLogo ?? undefined} height={200} />

      {!loginSent && (
        <form
          onSubmit={handleSendCode}
          className="w-full max-w-[400px]"
          noValidate
        >
          <h2 className="mb-6 text-center text-lg font-semibold">{t("login.title")}</h2>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-2 py-1 text-sm text-danger">{error}</div>
          )}

          <input
            type="email"
            className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-3 text-base transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
            placeholder={t("login.placeholder")}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />

          <button
            type="submit"
            className="mb-4 w-full rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-accent-hover"
            disabled={loading}
          >
            {loading ? t("login.actions.sending") : t("login.actions.continue")}
          </button>

          <p className="text-center text-sm leading-snug text-muted">
            {t("login.footer")}
          </p>
        </form>
      )}

      {loginSent && (
        <form
          onSubmit={handleVerify}
          className="w-full max-w-[400px]"
          noValidate
        >
          <h2 className="mb-6 text-lg font-semibold">{t("confirm.title")}</h2>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-2 py-1 text-sm text-danger">
              {error === "Invalid code" ? t("confirm.errorInvalid") : error}
            </div>
          )}

          <input
            type="text"
            className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-3 text-base transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
            placeholder={t("confirm.placeholder")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />

          <button
            type="submit"
            className="mb-4 w-full rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-black transition-colors hover:bg-accent-hover"
            disabled={loading}
          >
            {loading
              ? t("confirm.actions.verifying")
              : t("confirm.actions.verify")}
          </button>

          <p className="text-center text-sm leading-snug text-muted">
            {t("confirm.help")}{" "}
            <span
              onClick={handleTryAgain}
              className="cursor-pointer font-bold text-primary"
            >
              {t("confirm.actions.tryAgain")}
            </span>
          </p>
        </form>
      )}
    </div>
  );
};

export default Login;
